import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

// Hooks
import useLabels from '@/utils/hooks/swr/useLabels';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { deleteApi } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useWindowSize from '@/utils/hooks/utils/useWindowSize';

// Lib
import {
    DELETE_ERROR,
    DELETE_FROM_MEDIA_ERROR,
    DELETE_FROM_MEDIA_SUCCESS,
    DELETE_SUCCESS
} from '@/lib/posts/postLabelsAlbumsEvents';
import { LABEL_ATTACH_OR_DETACH_TO_MEDIA, LABELS_DELETE } from '@/lib/routes/apiEndpoints';

// Components
import Dropdown, { BaseItem } from '@/components/base/Dropdown';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import ActionTag from '@/components/base/Tag/ActionTag';
import { ActionButton } from '@/components/composite/MediaViewer/components/LabelsAlbums/ActionButton';
const CustomPopover = dynamic(() => import('@/components/composite/Popover/CustomPopover'));
const LabelsPopover = dynamic(() => import('@/components/composite/Post/LabelsPopover'));

// Types
import { CommonService } from '@/utils/services/CommonService';

import PropTypes from 'prop-types';

import { STORY } from '@/lib/posts/postTypes';

export const LabelsSection = ({
    socialProfile,
    post,
    handleLabelsEvent,
    isOpenLabels,
    toggle,
    postType,
    disabled
}) => {
    const [labelDeleting, setLabelDeleting] = useState(null);
    const { width: windowWidth } = useWindowSize();
    const [options, setOptions] = useState({
        placement: 'right-end',
        offset: [40, 20]
    });
    const { t } = useTranslation('commons');
    const network = socialProfile?.social_network_type;
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: options?.placement,
        isReferenceHidden: true,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: options?.offset
                }
            }
        ]
    });

    const { labels, mutateLabels, requestParams, setRequestParams } = useLabels({
        activeSPID: socialProfile?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false }
    });

    const updatePopperPosition = useCallback(async () => {
        if (typeof update === 'function') await update();
    }, [update]);

    useEffect(() => {
        if (postType === 'story' && network === 'instagram') {
            setRequestParams({ media_type: 'stories' });
        }
        if (
            (postType !== 'story' && requestParams?.media_type) ||
            (network !== 'instagram' && requestParams?.media_type)
        ) {
            setRequestParams(undefined);
        }
    }, [postType]);

    useEffect(() => {
        if (popperElement) {
            updatePopperPosition();
        }
    }, [updatePopperPosition, post?.labels, popperElement]);

    useEffect(() => {
        if (windowWidth > 675) {
            if (options?.placement !== 'right-end') {
                setOptions({
                    placement: 'right-end',
                    offset: [40, 20]
                });
            }
        } else {
            if (options?.placement !== 'bottom-start') {
                setOptions({
                    placement: 'bottom-start',
                    offset: [0, -25]
                });
            }
        }
    }, [windowWidth]);

    const handleDeleteLabelFromMedia = async (data) => {
        try {
            setLabelDeleting(data?.label);
            await deleteApi(
                injectEndpointUrlParams(LABEL_ATTACH_OR_DETACH_TO_MEDIA, {
                    SPID: socialProfile?.id,
                    MEDIA_TYPE: postType === STORY ? 'stories' : 'medias',
                    MEDIA_ID: post?.id
                }),
                { data: { label_id: data.id } }
            );
            handleLabelsEvent(data, DELETE_FROM_MEDIA_SUCCESS, post?.id);
        } catch (e) {
            handleLabelsEvent(data, DELETE_FROM_MEDIA_ERROR, post?.id);
        } finally {
            setLabelDeleting(null);
        }
    };

    const handleDeleteLabel = async (data) => {
        try {
            setLabelDeleting(data?.label);
            await deleteApi(
                injectEndpointUrlParams(LABELS_DELETE, {
                    SPID: socialProfile?.id,
                    LABEL_ID: data?.id || data?.label
                })
            );
            handleLabelsEvent(data, DELETE_SUCCESS, post?.id);
            mutateLabels(
                CommonService.formatLabelOptions(labels)?.filter(
                    (element) => element.value !== data?.value
                ),
                true
            );
        } catch (e) {
            handleLabelsEvent(data, DELETE_ERROR, post?.id);
        } finally {
            setLabelDeleting(null);
        }
    };
    return (
        <>
            {post?.labels?.map((item, idx) => (
                <div className="mb-2 ml-2" key={idx} data-testid={`label-${idx}`}>
                    <Dropdown
                        button={
                            <Dropdown.Button
                                className="rounded-full focus:ring-offset-0"
                                disabled={labelDeleting === item?.label}>
                                <ActionTag icon="labels">
                                    <span className="capitalize">{item?.label}</span>
                                    {labelDeleting === item?.label ? (
                                        <Loader className="ml-1 h-3 w-3 text-icoBlue" />
                                    ) : (
                                        <Icon
                                            icon="chevron-bottom"
                                            data-testid="chevron-icon"
                                            className="ml-1 text-xxs"
                                        />
                                    )}
                                </ActionTag>
                            </Dropdown.Button>
                        }
                        position="center">
                        <Dropdown.Divider>
                            <Dropdown.Item
                                onClick={() => handleDeleteLabelFromMedia(item)}
                                data-testid="delete-from-media">
                                {({ active }) => (
                                    <BaseItem active={active}>
                                        <span className={clsx('text-icoGray-500')}>
                                            {t('media.labels.remove_label_from_media')}
                                        </span>
                                    </BaseItem>
                                )}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => handleDeleteLabel(item)}
                                data-testid="delete">
                                {({ active }) => (
                                    <BaseItem active={active}>
                                        <span className={clsx('text-icoGray-500')}>
                                            {t('media.labels.delete_label')}
                                        </span>
                                    </BaseItem>
                                )}
                            </Dropdown.Item>
                        </Dropdown.Divider>
                    </Dropdown>
                </div>
            ))}
            <ActionButton
                className={clsx('mb-2 ml-2', {
                    'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20': !disabled
                })}
                icon="labels"
                onClick={() => toggle(!isOpenLabels)}
                aria-label="add-label"
                setReferenceElement={setReferenceElement}
                title={t('commons:media.labels.add_labels')}
                disabled={disabled}
            />
            {isOpenLabels
                ? ReactDOM.createPortal(
                      <CustomPopover
                          open={isOpenLabels}
                          popperElement={popperElement}
                          setPopperElement={setPopperElement}
                          toggle={() => toggle(false)}
                          styles={styles}
                          attributes={attributes}
                          isBorder
                          width="w-80 max-w-[calc(100vw-38px)]"
                          wrapperClassName="z-1">
                          <LabelsPopover
                              labelsOnMedia={post?.labels}
                              mediaId={post?.id}
                              postType={postType}
                              handleLabelsEvent={handleLabelsEvent}
                              socialProfile={socialProfile}
                              type="mediaViewer"
                          />
                      </CustomPopover>,
                      document.getElementById('media-viewer-content')
                  )
                : null}
        </>
    );
};

LabelsSection.propTypes = {
    socialProfile: PropTypes.object,
    post: PropTypes.object,
    handleLabelsEvent: PropTypes.func,
    isOpenLabels: PropTypes.bool,
    toggle: PropTypes.func,
    postType: PropTypes.string
};
